import { apiClient } from './client'
import type { Tenant, ApiResponse } from '@/types'

/**
 * Tenant lifecycle (list/detail/onboard/update/deactivate/test-auth), scan
 * orchestration (trigger/status/limits/recent jobs), the admin job-queue
 * surfaces, and tenant-scoped operational settings (scan cadence, user-
 * insights inactivity threshold, monitored-resource selection). Split out
 * of `lib/api.ts` (WO-10.4).
 */

// Tenant APIs
export async function getTenants(): Promise<Tenant[]> {
  const response = await apiClient.get<ApiResponse<Tenant[]>>('/tenants')
  return response.data.data
}

export async function getTenant(id: string): Promise<Tenant> {
  const response = await apiClient.get<ApiResponse<Tenant>>(`/tenants/${id}`)
  return response.data.data
}

// Tenant scan settings APIs
export async function getTenantScanSettings(tenantId: string): Promise<{
  tenantId: string
  scanFrequency: string
  scanScheduleEnabled: boolean
}> {
  const response = await apiClient.get<{
    tenantId: string
    scanFrequency: string
    scanScheduleEnabled: boolean
  }>(`/tenants/${tenantId}/scan-settings`)
  return response.data
}

export async function updateTenantScanSettings(tenantId: string, settings: {
  scanFrequency: string
  scanScheduleEnabled: boolean
}): Promise<{
  tenantId: string
  scanFrequency: string
  scanScheduleEnabled: boolean
}> {
  const response = await apiClient.post<{
    tenantId: string
    scanFrequency: string
    scanScheduleEnabled: boolean
  }>(`/tenants/${tenantId}/scan-settings`, settings)
  return response.data
}

// User Insights settings APIs (tenant-scoped inactivity threshold)
export async function getUserInsightsSettings(tenantId: string): Promise<{ tenantId: string; inactivityThresholdDays: number }> {
  const response = await apiClient.get<{ tenantId: string; inactivityThresholdDays: number }>(
    `/tenants/${tenantId}/user-insights-settings`
  )
  return response.data
}

export async function updateUserInsightsSettings(
  tenantId: string,
  settings: { inactivityThresholdDays: number }
): Promise<{ tenantId: string; inactivityThresholdDays: number }> {
  const response = await apiClient.post<{ tenantId: string; inactivityThresholdDays: number }>(
    `/tenants/${tenantId}/user-insights-settings`,
    settings
  )
  return response.data
}

export async function scanTenant(id: string): Promise<{jobId: string, tenantId: string, status: string, message: string, timestamp: string}> {
  // Returns immediately with job ID for async tracking
  const response = await apiClient.post(`/scan/tenant/${id}`, {}, {
    timeout: 30000 // 30 second timeout for job creation
  })
  return response.data
}

export async function getScanJobStatus(jobId: string): Promise<{
  id: string
  tenantId: string
  jobType: string
  status: string
  progressPercentage: number
  currentStep?: string
  statusMessage?: string
  createdAt: string
  startedAt?: string
  completedAt?: string
  errorMessage?: string
  resultData?: string
  createdBy: string
}> {
  const response = await apiClient.get(`/scan/job/${jobId}`)
  return response.data
}

/**
 * Lightweight scan-job row from `GET /scan/tenant/{id}/jobs`
 * (`ScanJobSummaryDTO` — same shape as `getScanJobStatus` above minus the
 * 4-5MB `resultData` field, which that endpoint deliberately excludes).
 */
export interface ScanJobSummary {
  id: string
  tenantId: string
  jobType: string
  status: string
  statusMessage?: string
  progressPercentage: number
  currentStep?: string
  createdAt: string
  startedAt?: string
  completedAt?: string
  errorMessage?: string
  createdBy: string
}

export async function getRecentScanJobs(tenantId: string, limit: number = 10): Promise<ScanJobSummary[]> {
  const response = await apiClient.get<ScanJobSummary[]>(`/scan/tenant/${tenantId}/jobs?limit=${limit}`)
  return response.data
}

/** `GET /scan/tenant/{id}/limits` response (`ScanController.GetScanLimits`, an anonymous camelCase object). */
export interface ScanLimitsInfo {
  tenantId: string
  canScan: boolean
  dailyLimit: number
  currentCount: number
  remainingScans: number
  resetTime: string
  reasonBlocked?: string | null
}

export async function getScanLimits(tenantId: string): Promise<ScanLimitsInfo> {
  const response = await apiClient.get<ScanLimitsInfo>(`/scan/tenant/${tenantId}/limits`)
  return response.data
}

// Tenant Management APIs
//
// `TenantManagementListItem`/`TenantDetail` mirror `TenantSummaryDto` /
// `TenantDetailsDto` (`TenantManagementController.cs`) — both camelCase per
// the Container's global JSON naming policy. `TenantOnboardingPayload` /
// `TenantUpdatePayload` mirror `TenantOnboardingRequest` / `TenantUpdateRequest`
// on the same controller.
//
// Optional fields are plain `string?` (no `| null`) — the Container's JSON
// options set `DefaultIgnoreCondition = WhenWritingNull` (`Program.cs:93`),
// so a null C# property is OMITTED from the response, never sent as a
// literal JSON `null`. `string | undefined` is the accurate wire type.
export interface TenantManagementListItem {
  id: string
  tenantId: string
  name: string
  domain?: string
  onboardingStatus?: string
  notificationEmail?: string
  autoTaskEmail?: string
  rejectedDriftNotificationEmail?: string
  driftAlertNotificationEmail?: string
  isActive: boolean
  createdAt: string
  lastScanAt: string
  hasAuthConfig: boolean
  // C# models this as a plain `string` (default `"PerTenant"`), but by
  // convention only ever holds one of these two values — matches
  // `AdminConsentStatus.authenticationMode` (admin.ts), the type already
  // used for the same concept elsewhere in this API client.
  authenticationMode: 'PerTenant' | 'MultiTenant'
  adminConsentGrantedAt?: string
  adminConsentGrantedBy?: string
}

export interface TenantDetail extends Omit<TenantManagementListItem, 'hasAuthConfig'> {
  clientId?: string
  certificateThumbprint?: string
  authConfigValid: boolean
}

export interface TenantOnboardingPayload {
  tenantId: string
  name: string
  domain?: string
  clientId?: string
  certificateThumbprint?: string
  authenticationMode: string
  notificationEmail?: string
  autoTaskEmail?: string
  rejectedDriftNotificationEmail?: string
  driftAlertNotificationEmail?: string
}

export interface TenantUpdatePayload {
  name: string
  domain?: string
  clientId?: string
  certificateThumbprint?: string
  notificationEmail?: string
  autoTaskEmail?: string
  rejectedDriftNotificationEmail?: string
  driftAlertNotificationEmail?: string
  // Optional: `TenantUpdateRequest.IsActive` defaults to `true` server-side
  // (`TenantManagementController.cs:664`) when the field is omitted from
  // the request body.
  isActive?: boolean
  authenticationMode: string
}

export interface TenantAuthTestResult {
  tenantId: string
  success: boolean
  message: string
  testedAt: string
}

export async function getTenantManagementList(): Promise<TenantManagementListItem[]> {
  const response = await apiClient.get<TenantManagementListItem[]>('/tenantmanagement')
  return response.data
}

export async function onboardTenant(tenantData: TenantOnboardingPayload): Promise<TenantDetail> {
  const response = await apiClient.post<TenantDetail>('/tenantmanagement/onboard', tenantData)
  return response.data
}

export async function updateTenant(tenantId: string, tenantData: TenantUpdatePayload): Promise<TenantDetail> {
  const response = await apiClient.put<TenantDetail>(`/tenantmanagement/${tenantId}`, tenantData)
  return response.data
}

export async function deactivateTenant(tenantId: string): Promise<void> {
  await apiClient.delete(`/tenantmanagement/${tenantId}`)
}

export async function testTenantAuth(tenantId: string): Promise<TenantAuthTestResult> {
  const response = await apiClient.post<TenantAuthTestResult>(`/tenantmanagement/${tenantId}/test-auth`)
  return response.data
}

// Tenant Resources APIs
export async function getTenantMonitoredResources(tenantId: string): Promise<string[]> {
  const response = await apiClient.get<string[]>(`/tenants/${tenantId}/resources`)
  return response.data
}

export async function updateTenantMonitoredResources(tenantId: string, resources: string[]): Promise<void> {
  if (import.meta.env.DEV) {
    console.log('API updateTenantMonitoredResources called!');
    console.log('tenantId:', tenantId);
    console.log('resources:', resources);
    console.log('resources length:', resources.length);
  }

  try {
    await apiClient.put(`/tenants/${tenantId}/resources`, { resources })
    if (import.meta.env.DEV) {
      console.log('API PUT request completed successfully');
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('API PUT request failed:', error);
    }
    throw error;
  }
}

export async function getAvailableResources(): Promise<Record<string, string[]>> {
  const response = await apiClient.get<Record<string, string[]>>('/tenants/resources/available')
  return response.data
}

// Tenant Status APIs for concurrency control
export async function getTenantStatus(tenantId: string): Promise<{
  tenantId: string
  hasActiveOperations: boolean
  queuedJobs: Array<{
    jobId: string
    jobType: string
    queuedAt: string
    priority: number
  }>
  runningJobs: Array<{
    jobId: string
    jobType: string
    status: string
    progressPercentage: number
    currentStep?: string
    startedAt?: string
  }>
  queuePosition?: number
  timestamp: string
}> {
  const response = await apiClient.get(`/tenantstatus/${tenantId}/status`)
  return response.data
}

/** A running-job row inside `DetailedQueueStatus` (below). */
export interface QueueRunningJob {
  jobId: string
  tenantId: string
  jobType: string
  status: string
  createdBy: string
  startedAt?: string
  progressPercentage: number
  currentStep?: string
  statusMessage?: string
}

export async function getQueueStatus(): Promise<{
  queueLength: number
  runningJobsCount: number
  runningJobs: QueueRunningJob[]
  timestamp: string
}> {
  const response = await apiClient.get('/tenantstatus/queue/status')
  return response.data
}

export async function cancelQueuedJobs(tenantId: string): Promise<{
  tenantId: string
  message: string
  cancelledJobIds: string[]
  timestamp: string
}> {
  const response = await apiClient.post(`/tenantstatus/${tenantId}/cancel-queued`)
  return response.data
}

/**
 * `GET /tenantstatus/admin/queue/detailed` response
 * (`TenantStatusController.GetDetailedQueueStatus`). Mirrors the shape
 * `pages/AdminQueue.tsx` already declared locally for its `useQuery`
 * generic — kept structurally identical so that call site needs no change.
 */
export interface DetailedQueueStatus {
  summary: {
    queueLength: number
    runningJobsCount: number
    totalActiveJobs: number
  }
  queuedJobs: Array<{
    jobId: string
    tenantId: string
    jobType: string
    status: string
    createdBy: string
    createdAt: string
    startedAt?: string
    progressPercentage: number
    currentStep?: string
    statusMessage?: string
    estimatedWaitTime: number
  }>
  runningJobs: Array<{
    jobId: string
    tenantId: string
    jobType: string
    status: string
    createdBy: string
    startedAt?: string
    progressPercentage: number
    currentStep?: string
    statusMessage?: string
    runningDuration: number
  }>
  systemHealth: {
    isProcessorHealthy: boolean
    longestQueuedJob: number
    averageJobDuration: number
  }
  timestamp: string
}

// Admin Queue Management APIs
export async function getDetailedQueueStatus(): Promise<DetailedQueueStatus> {
  const response = await apiClient.get<DetailedQueueStatus>('/tenantstatus/admin/queue/detailed')
  return response.data
}

export async function emergencyStop(): Promise<{
  message: string
  cancelledJobIds: string[]
  note: string
  timestamp: string
}> {
  const response = await apiClient.post('/tenantstatus/admin/emergency-stop')
  return response.data
}

export async function clearStuckJobs(): Promise<{
  message: string
  clearedJobIds: string[]
  timestamp: string
}> {
  const response = await apiClient.post('/tenantstatus/admin/clear-stuck-jobs')
  return response.data
}

// Admin Scan Errors Dashboard API
export async function getAdminScanErrors(hoursBack: number = 24): Promise<{
  summary: {
    totalErrors: number
    totalFailed: number
    totalPartial: number
    tenantsAffected: number
    hoursBack: number
  }
  tenantGroups: Array<{
    tenantId: string
    tenantName: string
    tenantDomain: string
    errorCount: number
    failedCount: number
    partialCount: number
    jobs: Array<{
      id: string
      jobType: string
      status: string
      statusMessage?: string
      progressPercentage: number
      currentStep?: string
      createdAt: string
      startedAt?: string
      completedAt?: string
      errorMessage?: string
      createdBy: string
    }>
  }>
  timestamp: string
}> {
  const response = await apiClient.get(`/scan/admin/errors?hoursBack=${hoursBack}`)
  return response.data
}

export const tenantsApi = {
  getTenants,
  getTenant,
  getTenantScanSettings,
  updateTenantScanSettings,
  getUserInsightsSettings,
  updateUserInsightsSettings,
  scanTenant,
  getScanJobStatus,
  getRecentScanJobs,
  getScanLimits,
  getTenantManagementList,
  onboardTenant,
  updateTenant,
  deactivateTenant,
  testTenantAuth,
  getTenantMonitoredResources,
  updateTenantMonitoredResources,
  getAvailableResources,
  getTenantStatus,
  getQueueStatus,
  cancelQueuedJobs,
  getDetailedQueueStatus,
  emergencyStop,
  clearStuckJobs,
  getAdminScanErrors,
}
